import {
  PREVENT_OPTIN_OPEN,
  TRUE_STRING
} from '../const/common';
import { log } from '../utils/debug';

const DEFAULT_HOLIDAYS_COUNTRY = 'DE';

export function checkBannerOptinShow(usedBanner, context) {
  if (usedBanner.dontShowPushOptInDuringShown) {
    sessionStorage.setItem(PREVENT_OPTIN_OPEN, TRUE_STRING);
    context.confirm.hide();
  }
}

export function isWithinDateRange(banner = {}) {
  const now = new Date();
  const startsAt = banner.startsAt ? new Date(banner.startsAt) : null;
  const endsAt = banner.endsAt ? new Date(banner.endsAt) : null;

  return (!startsAt || startsAt < now) && (!endsAt || endsAt > now);
}

export async function todayIsHoliday(banner) {
  if (!banner?.hideBannerOnHolidays && !banner?.showBannerOnHolidays) {
    return false;
  }
  try {
    const { default: Holidays } = await import('date-holidays');
    let isHoliday = false;
    const today = new Date();
    const country = banner.holidaysCountry || DEFAULT_HOLIDAYS_COUNTRY;
    const states = banner.holidaysState;

    if (states?.length) {
      isHoliday = states.some((state) => {
        const holiday = new Holidays(country, state.value);
        return !!holiday.isHoliday(today);
      });
    } else {
      const holiday = new Holidays(country);
      isHoliday = !!holiday.isHoliday(today);
    }
    return isHoliday;
  } catch (error) {
    log.info(error);
    return false;
  }
}

export async function shouldShowBasedOnHoliday(banner) {
  const isHoliday = await todayIsHoliday(banner);

  // If banner should be hidden on holidays and today is a holiday, don't show
  if (banner?.hideBannerOnHolidays && isHoliday) {
    return false;
  }

  // If banner should only be shown on holidays and today is NOT a holiday, don't show
  if (banner?.showBannerOnHolidays && !isHoliday) {
    return false;
  }
  return true;
}

export function isCurrentTimeInTimeSlots(timetable) {
  if (!Object.keys(timetable || {}).length) {
    return true;
  }
  const currentDate = new Date();
  const currentDay = currentDate.getDay();
  const currentTime = currentDate.getHours() * 60 + currentDate.getMinutes();

  const timeSlots = timetable[currentDay];
  if (!timeSlots) {
    return false;
  }

  return timeSlots.some((slot) => {
    const startTime = slot.startHours * 60 + slot.startMinutes;
    const endTime = slot.endHours * 60 + slot.endMinutes;
    return currentTime >= startTime && currentTime <= endTime;
  });
}

export const carouselStyle = `
.carousel {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 100%;
    margin: 0 auto;
    overflow: hidden;
}

.carousel-track-container {
    overflow: hidden;
    width: calc(100% - 78px);
}

.carousel-track {
    display: flex;
    padding:0px;
    margin:0px
}

.carousel-slide {
    min-width: 100%;
    box-sizing: border-box;
    list-style: none;
    padding: 0;
    display: none; /* Hide all slides initially */
}

.carousel-slide.current-slide {
    display: block; /* Show only the current slide */
    animation: fadeIn 0.8s ease-in-out forwards;
}
/* Define the keyframes for the animation */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.carousel-button {
    background-color: rgba(0, 0, 0, 0.5);
    border: none;
    color: white;
    cursor: pointer;
    padding: 8px;
    margin: 8px;
}
.carousel-button.prev {
    position: absolute;
    left: -10px;
    z-index: 100;
}

.carousel-button.next {
    position: absolute;
    right: -10px;
}

.carousel-nav {
    display: flex;
    justify-content: center;
    padding: 10px;
}

.carousel-indicator {
    background-color: gray;
    border: none;
    border-radius: 100%;
    cursor: pointer;
    height: 10px;
    margin: 0 5px;
    width: 10px;
    padding: 0;
}

.carousel-indicator.current-slide {
    background-color: black;
}
`;

export function createCarousel(document, iframe, autoPlayInterval = 5) {
  const items = document.getElementsByClassName('cp-row-container');
  const itemsArray = Array.from(items);

  // Select the table element
  const tableElement = document.querySelector('table');

  // Check if the table element exists and set its display property to none
  if (tableElement) {
    tableElement.style.display = 'none';
  }

  // Create a container div element for the carousel
  const containerDiv = document.createElement('div');

  // Create the site container and carousel structure
  containerDiv.innerHTML = `
  <div class="carousel">
        <button class="carousel-button prev">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="white">
            <path d="M15.293 3.293 6.586 12l8.707 8.707 1.414-1.414L9.414 12l7.293-7.293-1.414-1.414z"/>
          </svg>
        </button>
        <div class="carousel-track-container">
            <ul class="carousel-track"></ul>
        </div>
        <button class="carousel-button next">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="white">
            <path d="M7.293 4.707 14.586 12l-7.293 7.293 1.414 1.414L17.414 12 8.707 3.293 7.293 4.707z"/>
          </svg>
        </button>
        <div class="carousel-nav"></div>
    </div>
`;

  // Append the container div to the body
  document.body.prepend(containerDiv);

  const track = document.querySelector('.carousel-track');
  const dotsNav = document.querySelector('.carousel-nav');
  const nextButton = document.querySelector('.carousel-button.next');
  const prevButton = document.querySelector('.carousel-button.prev');
  const customNextButton = document.querySelectorAll('a[href="#next-screen"]');
  const customPrevButton = document.querySelectorAll('a[href="#prev-screen"]');

  // Create slides and pagination dots dynamically
  itemsArray.forEach((item, index) => {
    // Create slide
    const slide = document.createElement('li');
    slide.classList.add('carousel-slide');
    if (index === 0) slide.classList.add('current-slide');
    const contentDiv = document.createElement('div');
    contentDiv.appendChild(item);
    slide.appendChild(contentDiv);
    track.appendChild(slide);

    // Create dot
    const dot = document.createElement('button');
    dot.classList.add('carousel-indicator');
    if (index === 0) dot.classList.add('current-slide');
    dotsNav.appendChild(dot);
  });

  const slides = Array.from(track.children);
  const dots = Array.from(dotsNav.children);

  const moveToSlide = (currentSlide, targetSlide) => {
    currentSlide.classList.remove('current-slide');
    targetSlide.classList.add('current-slide');
    updateDots(currentSlide, targetSlide);
    updateArrows();
    requestAnimationFrame(() => {
      const carousel = document.querySelector('.carousel');
      const height = `${carousel.offsetHeight}px`;
      iframe.style.height = height;
    });
  };

  const updateDots = (currentSlide, targetSlide) => {
    const currentDot = dotsNav.querySelector('.current-slide');
    const targetIndex = slides.indexOf(targetSlide);
    const targetDot = dots[targetIndex];

    currentDot.classList.remove('current-slide');
    targetDot.classList.add('current-slide');
  };

  const updateArrows = () => {
    const currentSlide = track.querySelector('.current-slide');
    const currentIndex = slides.indexOf(currentSlide);

    if (currentIndex === 0) {
      prevButton.style.display = 'none';
    } else {
      prevButton.style.display = 'block';
    }

    if (currentIndex === slides.length - 1) {
      nextButton.style.display = 'none';
    } else {
      nextButton.style.display = 'block';
    }
  };

  // Automatically move to the next slide every 1.5 seconds
  const startAutoSlide = () => setInterval(() => {
    const currentSlide = track.querySelector('.current-slide');
    const nextSlide = currentSlide.nextElementSibling || slides[0]; // Loop back to the first slide
    moveToSlide(currentSlide, nextSlide);
  }, autoPlayInterval * 1000);

  // Start automatic slide movement
  let autoSlideInterval = startAutoSlide();

  // Pause auto sliding on hover
  containerDiv.addEventListener('mouseover', () => {
    clearInterval(autoSlideInterval);
  });

  // Resume auto sliding when not hovering
  containerDiv.addEventListener('mouseout', () => {
    autoSlideInterval = startAutoSlide();
  });

  // When click left, move slides to the left
  prevButton.addEventListener('click', () => {
    const currentSlide = track.querySelector('.current-slide');
    const prevSlide = currentSlide.previousElementSibling;

    if (prevSlide) {
      moveToSlide(currentSlide, prevSlide);
    }
  });

  // When click right, move slides to the right
  nextButton.addEventListener('click', () => {
    const currentSlide = track.querySelector('.current-slide');
    const nextSlide = currentSlide.nextElementSibling;

    if (nextSlide) {
      moveToSlide(currentSlide, nextSlide);
    }
  });

  // When  click custom Prev, move slides to the left
  if (customPrevButton.length > 0) {
    customPrevButton.forEach((button) => {
      // Check if the button has the target="_blank" attribute
      if (button.getAttribute('target') === '_blank') {
        // Remove the target attribute
        button.setAttribute('target', '_top');
      }

      button.addEventListener('click', (e) => {
        e.preventDefault();
        const currentSlide = track.querySelector('.current-slide');
        const prevSlide = currentSlide.previousElementSibling;

        if (prevSlide) {
          moveToSlide(currentSlide, prevSlide);
        }
      });
    });
  }

  // When click custom Next, move slides to the right
  if (customNextButton.length > 0) {
    customNextButton.forEach((button) => {
      // Check if the button has the target="_blank" attribute
      if (button.getAttribute('target') === '_blank') {
        // Remove the target attribute
        button.setAttribute('target', '_top');
      }

      button.addEventListener('click', (e) => {
        e.preventDefault();
        const currentSlide = track.querySelector('.current-slide');
        const nextSlide = currentSlide.nextElementSibling;

        if (nextSlide) {
          moveToSlide(currentSlide, nextSlide);
        }
      });
    });
  }

  // When click the nav indicators, move to that slide
  dotsNav.addEventListener('click', (e) => {
    const targetDot = e.target.closest('button');

    if (!targetDot) return;

    const currentSlide = track.querySelector('.current-slide');
    const targetIndex = dots.findIndex((dot) => dot === targetDot);
    const targetSlide = slides[targetIndex];

    moveToSlide(currentSlide, targetSlide);
  });

  updateArrows();
}
